import {
  clear,
  createStore,
  del,
  delMany,
  entries,
  get,
  keys,
  set,
  setMany,
  values
} from 'idb-keyval'

import { logErrorOnce, noop } from '../helper'
import { toJSON } from '../tojson'
import { version } from '../version'

import type { UseStore } from 'idb-keyval'

export class Storage {
  static accessKey = '__last_access_time__'

  static delay_10s = 10 * 1000

  static expired_30d = 30 * 24 * 60 * 60 * 1000

  static prefix = '@rehaje/storage'

  idbStore?: UseStore

  constructor(table: string) {
    if (typeof globalThis.indexedDB === 'object') {
      try {
        const dbName = `${Storage.prefix}/${table}@${version}`
        this.idbStore = createStore(dbName, 'table')
        this.updateLastAccessTime()
        setTimeout(Storage.delExpiredStorage, Storage.delay_10s)
        Storage.delExpiredStorage = noop
      } catch (error) {
        logErrorOnce('Failed to initialize IndexedDB storage', error)
      }
    }
  }

  static async delExpiredStorage() {
    if (typeof globalThis.indexedDB === 'object') {
      try {
        for (const { name } of await globalThis.indexedDB.databases()) {
          try {
            if (name?.startsWith(Storage.prefix)) {
              const accessTime = await get(
                Storage.accessKey,
                createStore(name, 'table')
              )
              if (
                !Number.isFinite(accessTime) ||
                Date.now() - accessTime > Storage.expired_30d
              ) {
                globalThis.indexedDB.deleteDatabase(name)
              }
            }
          } catch (error) {
            logErrorOnce('Failed to delete expired storage', error)
          }
        }
      } catch (error) {
        logErrorOnce('Failed to enumerate IndexedDB databases', error)
      }
    }
  }

  async clear(): Promise<void> {
    if (this.idbStore) {
      try {
        await clear(this.idbStore)
      } catch (error) {
        logErrorOnce('Failed to clear IndexedDB storage', error)
      }
    }
  }

  async del(key: IDBValidKey): Promise<void> {
    if (this.idbStore) {
      try {
        await del(key, this.idbStore)
      } catch (error) {
        logErrorOnce('Failed to delete from IndexedDB storage', error)
      }
    }
  }

  async delMany(keys: IDBValidKey[]): Promise<void> {
    if (this.idbStore) {
      try {
        await delMany(keys, this.idbStore)
      } catch (error) {
        logErrorOnce(
          'Failed to delete multiple items from IndexedDB storage',
          error
        )
      }
    }
  }

  async entries<K extends IDBValidKey, T>(): Promise<[K, T][]> {
    if (this.idbStore) {
      try {
        return await entries(this.idbStore)
      } catch (error) {
        logErrorOnce('Failed to read entries from IndexedDB storage', error)
      }
    }
    return []
  }

  async get<T>(key: IDBValidKey): Promise<T | undefined> {
    if (this.idbStore) {
      try {
        return await get(key, this.idbStore)
      } catch (error) {
        logErrorOnce('Failed to get value from IndexedDB storage', error)
      }
    }
  }

  async keys<K extends IDBValidKey>(): Promise<K[]> {
    if (this.idbStore) {
      try {
        return await keys(this.idbStore)
      } catch (error) {
        logErrorOnce('Failed to read keys from IndexedDB storage', error)
      }
    }
    return []
  }

  async set(key: IDBValidKey, value: any): Promise<void> {
    if (this.idbStore) {
      try {
        await set(key, toJSON(value), this.idbStore)
      } catch (error) {
        logErrorOnce('Failed to set value in IndexedDB storage', error)
      }
    }
  }

  async setMany(entries: [IDBValidKey, any][]): Promise<void> {
    if (this.idbStore) {
      try {
        await setMany(entries, this.idbStore)
      } catch (error) {
        logErrorOnce(
          'Failed to set multiple values in IndexedDB storage',
          error
        )
      }
    }
  }

  async values<T>(): Promise<T[]> {
    if (this.idbStore) {
      try {
        return await values(this.idbStore)
      } catch (error) {
        logErrorOnce('Failed to read values from IndexedDB storage', error)
      }
    }
    return []
  }

  private async updateLastAccessTime(): Promise<void> {
    if (this.idbStore) {
      try {
        await set(Storage.accessKey, Date.now(), this.idbStore)
      } catch (error) {
        logErrorOnce(
          'Failed to update last access time in IndexedDB storage',
          error
        )
      }
    }
  }
}
